import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileSpreadsheet, UserPlus } from 'lucide-react';
import { parsePeopleSoftRoster } from '../utils/rosterParser';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './Dialog';

const DEFAULT_DOMAIN = 'udst.edu.qa';

// Aligned columns shared by the header and every row: Select · ID · Name · Email · Status
const GRID_COLUMNS = '36px 104px minmax(150px, 1.1fr) minmax(190px, 1.4fr) 104px';

/** Checkbox that supports the indeterminate (partial-selection) visual state. */
function TriStateCheckbox({ checked, indeterminate, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && indeterminate;
  }, [checked, indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} {...props} />;
}

/**
 * Modal for importing a section roster from a PeopleSoft Excel export.
 *
 * Parses the file in the browser, previews the parsed students (withdrawn rows
 * shown but pre-excluded), lets faculty choose an email domain, then hands the
 * selected students back to the parent via onImport so the existing
 * "add students" flow can persist them. No server-side upload.
 *
 * @param {object}   props
 * @param {Function} props.onClose  - called to dismiss the modal
 * @param {Function} props.onImport - called with [{ studentId, name, email }]
 */
function RosterImportModal({ onClose, onImport }) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [rows, setRows] = useState([]); // { studentId, name, status, withdrawn, included }
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    // allow re-selecting the same file later
    e.target.value = '';
    if (!file) return;

    setError('');
    setRows([]);
    setFileName(file.name);

    try {
      const buffer = await file.arrayBuffer();
      const { students } = parsePeopleSoftRoster(buffer);
      if (students.length === 0) {
        setError('No students were found in the file.');
        return;
      }
      // Withdrawn students are shown but excluded by default.
      setRows(students.map((s) => ({ ...s, included: !s.withdrawn })));
    } catch (err) {
      setError(err.message || 'Failed to read the file.');
    }
  };

  const toggleRow = (studentId) => {
    setRows((prev) => prev.map((r) => (r.studentId === studentId ? { ...r, included: !r.included } : r)));
  };

  const setAll = (included) => {
    setRows((prev) => prev.map((r) => ({ ...r, included })));
  };

  const selected = rows.filter((r) => r.included);
  const withdrawnCount = rows.filter((r) => r.withdrawn).length;
  const cleanDomain = domain.trim().replace(/^@/, '');
  const domainValid = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(cleanDomain);
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const handleImport = () => {
    if (selected.length === 0) {
      setError('Select at least one student to import.');
      return;
    }
    if (!domainValid) {
      setError('Enter a valid email domain (e.g. udst.edu.qa).');
      return;
    }
    onImport(
      selected.map((r) => ({
        studentId: r.studentId,
        name: r.name || undefined,
        email: `${r.studentId}@${cleanDomain}`,
      }))
    );
  };

  const cellEllipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <Dialog size="xl" onClose={onClose} labelledBy="roster-import-title" zIndex={1100} isDirty={rows.length > 0}>
      <DialogHeader id="roster-import-title" title="Import Roster from Excel" icon={<FileSpreadsheet size={20} />} onClose={onClose} />

      <DialogBody style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {error && (
            <div role="alert" style={{
              padding: '8px 12px', background: 'var(--btn-danger-bg)',
              border: '1px solid var(--accent-danger)', borderRadius: 6,
              marginBottom: 16, color: 'var(--accent-danger)', fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.csv,.htm,.html"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Upload + domain controls share one row once a file is loaded */}
          <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap',
            marginBottom: rows.length > 0 ? 16 : 0,
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                PeopleSoft roster file
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                >
                  <Upload size={14} /> {fileName ? 'Replace file' : 'Choose file'}
                </button>
                {fileName && (
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 200, ...cellEllipsis }} title={fileName}>
                    {fileName}
                  </span>
                )}
              </div>
            </div>

            {rows.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Email domain
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  border: '1px solid var(--border-primary)', borderRadius: 6,
                  background: 'var(--bg-tertiary)', overflow: 'hidden', height: 36,
                }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', padding: '0 4px 0 10px' }}>
                    {'{id}'}@
                  </span>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder={DEFAULT_DOMAIN}
                    style={{
                      width: 200, height: '100%', border: 'none', background: 'transparent',
                      fontFamily: 'var(--font-mono)', fontSize: 13, padding: '0 10px 0 0',
                      color: domainValid ? 'var(--text-primary)' : 'var(--accent-danger)',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {rows.length === 0 && !error && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12, maxWidth: '70ch' }}>
              Upload the class roster exported from PeopleSoft ("Download to Excel"). The export has no
              email column, so addresses are built from each student ID. Withdrawn students are listed but
              excluded by default.
            </p>
          )}

          {rows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{selected.length}</strong> of {rows.length} selected
                  {withdrawnCount > 0 && (
                    <span style={{ color: 'var(--text-muted)' }}> · {withdrawnCount} withdrawn excluded</span>
                  )}
                </span>
              </div>

              <div style={{
                border: '1px solid var(--border-primary)', borderRadius: 8,
                overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0,
              }}>
                {/* Sticky header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: GRID_COLUMNS, alignItems: 'center',
                  gap: 12, padding: '10px 14px', background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-primary)',
                  fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  <TriStateCheckbox
                    checked={allSelected}
                    indeterminate={selected.length > 0}
                    onChange={() => setAll(!allSelected)}
                    aria-label={allSelected ? 'Deselect all students' : 'Select all students'}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>ID</span>
                  <span>Name</span>
                  <span>Email</span>
                  <span style={{ textAlign: 'right' }}>Status</span>
                </div>

                {/* Rows */}
                <div className="roster-import-rows" style={{ overflowY: 'auto', maxHeight: '46vh' }}>
                  {rows.map((r, i) => (
                    <label
                      key={r.studentId}
                      style={{
                        display: 'grid', gridTemplateColumns: GRID_COLUMNS, alignItems: 'center', gap: 12,
                        padding: '9px 14px',
                        borderBottom: i < rows.length - 1 ? '1px solid var(--border-secondary)' : 'none',
                        background: r.included ? 'var(--badge-info-bg)' : 'transparent',
                        fontSize: 13, cursor: 'pointer', opacity: r.included ? 1 : 0.65,
                        transition: 'background 120ms ease, opacity 120ms ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={r.included}
                        onChange={() => toggleRow(r.studentId)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-primary)', ...cellEllipsis }}>
                        {r.studentId}
                      </span>
                      <span style={{ color: 'var(--text-primary)', ...cellEllipsis }} title={r.name}>
                        {r.name || '—'}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)', ...cellEllipsis }}
                        title={`${r.studentId}@${cleanDomain}`}>
                        {r.studentId}@{cleanDomain}
                      </span>
                      <span style={{ textAlign: 'right' }}>
                        {r.withdrawn ? (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: 'var(--badge-danger-bg)', color: 'var(--accent-danger)',
                          }}>
                            {r.status || 'Withdrawn'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Enrolled</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
      </DialogBody>

      <DialogFooter>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={handleImport}
          disabled={selected.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
        >
          <UserPlus size={14} /> Add {selected.length > 0 ? selected.length : ''} {selected.length === 1 ? 'Student' : 'Students'}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

export default RosterImportModal;
